import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { GripVertical, Sparkles, Smartphone } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const IPhoneVideoSlider = () => {
  const { t } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const videoRef1 = useRef(null);
  const videoRef2 = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  // AI enhanced video (After - right side)
  const afterVideoUrl = 'https://av-menurank.s3.ap-south-1.amazonaws.com/Screenrecording_20260202_163405.mp4';
  // Normal/Original video (Before - left side)
  const beforeVideoUrl = 'https://av-menurank.s3.ap-south-1.amazonaws.com/Screenrecording_20260202_163445.mp4';

  // Sync videos on load
  useEffect(() => {
    const syncVideos = () => {
      if (videoRef1.current && videoRef2.current) {
        videoRef2.current.currentTime = videoRef1.current.currentTime;
      }
    };

    const video1 = videoRef1.current;
    if (video1) {
      video1.addEventListener('timeupdate', syncVideos);
      return () => video1.removeEventListener('timeupdate', syncVideos);
    }
  }, []);

  const handleMove = (clientY) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));
    const percent = Math.max(5, Math.min(95, (y / rect.height) * 100));
    setSliderPosition(percent);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    // Prevent page scroll while dragging vertically
    e.preventDefault();
    handleMove(e.touches[0].clientY);
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      // Prevent body scroll while dragging
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <section
      id="video-comparison"
      ref={sectionRef}
      className="py-20 md:py-32 relative overflow-hidden"
      data-testid="video-comparison-section"
    >
      {/* Background Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#F59E0B]/10 to-[#FFB703]/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#FFB703] text-sm font-medium uppercase tracking-wider mb-4 block">
            {t.videoSlider.label}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">
            {t.videoSlider.title} <span className="text-gradient">{t.videoSlider.titleHighlight}</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t.videoSlider.subtitle}
          </p>
        </motion.div>

        {/* iPhone Frame Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center"
        >
          {/* iPhone 15 Pro Frame */}
          <div className="relative">
            {/* Outer Glow */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#F59E0B]/20 to-[#FFB703]/10 rounded-[60px] blur-2xl" />
            
            {/* iPhone Body */}
            <div
              className="relative w-[280px] sm:w-[320px] h-[570px] sm:h-[650px] bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[50px] p-[12px] shadow-2xl border border-white/20"
              style={{
                boxShadow: '0 0 0 2px #1a1a1a, 0 0 0 4px #3a3a3e, 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              }}
            >
              {/* Side Buttons - Left */}
              <div className="absolute -left-[3px] top-[120px] w-[3px] h-[30px] bg-[#3a3a3e] rounded-l-sm" />
              <div className="absolute -left-[3px] top-[170px] w-[3px] h-[60px] bg-[#3a3a3e] rounded-l-sm" />
              <div className="absolute -left-[3px] top-[240px] w-[3px] h-[60px] bg-[#3a3a3e] rounded-l-sm" />
              
              {/* Side Button - Right */}
              <div className="absolute -right-[3px] top-[180px] w-[3px] h-[80px] bg-[#3a3a3e] rounded-r-sm" />

              {/* Screen Area */}
              <div
                ref={containerRef}
                className="relative w-full h-full bg-black rounded-[38px] overflow-hidden cursor-ns-resize select-none"
                onMouseDown={() => setIsDragging(true)}
                onTouchStart={() => setIsDragging(true)}
              >
                {/* Dynamic Island */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[100px] h-[32px] bg-black rounded-full z-30 flex items-center justify-center">
                  <div className="w-[10px] h-[10px] bg-[#1a1a1a] rounded-full mr-8" />
                </div>

                {/* Layer 1: After Video (Base - Full Color) */}
                <div className="absolute inset-0">
                  <video
                    ref={videoRef1}
                    src={afterVideoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {/* After Badge */}
                  <div className="absolute bottom-6 right-3 bg-gradient-to-r from-[#F59E0B] to-[#FFB703] px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg z-20">
                    <Sparkles className="w-3 h-3 text-[#0d0a07]" />
                    <span className="text-[10px] font-semibold text-[#0d0a07]">{t.videoSlider.after}</span>
                  </div>
                </div>

                {/* Layer 2: Before Video (Clipped - Filtered) */}
                <div
                  className="absolute inset-0 overflow-hidden z-10"
                  style={{ clipPath: `inset(0 0 ${100 - sliderPosition}% 0)` }}
                >
                  <video
                    ref={videoRef2}
                    src={beforeVideoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ filter: 'grayscale(100%) brightness(70%) sepia(20%)' }}
                  />
                  {/* Before Badge */}
                  <div className="absolute bottom-6 left-3 bg-white/10 backdrop-blur-md border border-white/20 px-2.5 py-1 rounded-full flex items-center gap-1 z-20">
                    <Smartphone className="w-3 h-3 text-white/70" />
                    <span className="text-[10px] font-medium text-white/70">{t.videoSlider.before}</span>
                  </div>
                </div>

                {/* Slider Handle */}
                <div
                  className="absolute left-0 right-0 h-[2px] bg-white z-20"
                  style={{ top: `${sliderPosition}%`, transform: 'translateY(-50%)' }}
                >
                  {/* Handle Grip */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-5 h-5 text-gray-600 rotate-90" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Instructions */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-white/40 text-sm mt-8"
        >
          {t.videoSlider.instruction}
        </motion.p>
      </div>
    </section>
  );
};
